// Local user preferences (web equivalent of the persisted user slice +
// owlly.appearance AsyncStorage key on mobile).

export type AppearancePreference = 'system' | 'dark' | 'light';

const KEYS = {
  appearance: 'owlly.appearance',
  appLanguage: 'owlly.appLanguage',
  voiceLanguage: 'owlly.voiceLanguage',
  summaryLanguage: 'owlly.summaryLanguage',
};

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string | null): void => {
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  } catch {
    // Storage unavailable (private mode) — preferences just won't persist.
  }
};

export const getAppearance = (): AppearancePreference => {
  const value = read(KEYS.appearance);
  return value === 'dark' || value === 'light' ? value : 'system';
};

export const setAppearance = (pref: AppearancePreference): void =>
  write(KEYS.appearance, pref);

export const getAppLanguage = (): string | null => read(KEYS.appLanguage);
export const setAppLanguage = (code: string): void =>
  write(KEYS.appLanguage, code);

/** Spoken language of recordings (null = auto-detect). */
export const getVoiceLanguage = (): string | null => read(KEYS.voiceLanguage);
export const setVoiceLanguage = (code: string | null): void =>
  write(KEYS.voiceLanguage, code);

export const getSummaryLanguage = (): string | null =>
  read(KEYS.summaryLanguage);
export const setSummaryLanguage = (code: string | null): void =>
  write(KEYS.summaryLanguage, code);

/** Base language code of the browser (e.g. "en", "tr"). Falls back to "en". */
export const getBrowserLanguageCode = (): string =>
  (navigator.language || 'en').split('-')[0];

/**
 * Value snapshotted onto new note docs: the user's saved preference, or
 * null = Automatic — the backend mirrors the transcript language (matches
 * what the Settings select already displays for the unset state).
 */
export const resolveSummaryLanguage = (): string | null =>
  getSummaryLanguage();
